import {
  BuildingOffice2Icon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

import Button from "common/components/Button/Button";
import WorkSiteIcon from "components/Icon/icons/WorkSiteIcon";
import { Link } from "react-router-dom";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import { User, Site } from "types";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import useAuth from "common/hooks/useAuth";
import { useTranslation } from "react-i18next";
import Status from "common/components/Status/Status";
import getWording from "common/utils/wording";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import { Constants } from "constants/Constants";
import Icon, { IconNames } from "components/Icon/Icon";
import useOrganization from "common/hooks/useOrganization";
import dayjs from "dayjs";

const iconStyle = "w-4 h-4 text-white";

function getStatusIcon(iconName: IconNames, status: boolean, label: string) {
  return (
    <TooltipWrapper
      content={<span className="text-sm">{label}</span>}
      place="bottom"
    >
      <span className={`mx-1 p-1 ${status ? "opacity-100" : "opacity-30"}`}>
        <Icon name={iconName} className="w-5 h-5" />
      </span>
    </TooltipWrapper>
  );
}

function getStatusText(name: string, status: boolean) {
  return (
    <div className="flex flex-row items-center shrink-0 mx-1 px-1 opacity-100">
      <Icon
        name={status ? "CheckIcon" : "XMarkIcon"}
        className={status ? "w-4 h-4 text-green-600" : "w-4 h-4 text-red-600"}
      />
      {name}
    </div>
  );
}

function ItemFollowedSite({ site }: { site: Site }) {
  return (
    <div className="flex items-center">
      <div className="rounded-lg overflow-hidden w-8 h-8 min-w-[32px] mr-3">
        <ImageComponent
          image={
            site?.logo || site?.mainImage
              ? ImageFromStrapiMedia(site.logo ?? site.mainImage)?.uri
              : undefined
          }
        />
      </div>

      <span className="font-bold text-sm">{site?.name}</span>
    </div>
  );
}

function UserPublicItem({ item, onDelete, onNavigate }: any) {
  const { user: currentUser } = useAuth();
  const { organization } = useOrganization();

  const canEditItem = currentUser?.isAdmin || currentUser?.isSuperadmin;

  const {
    avatar,
    name: fullname,
    email,
    phoneNumber,
    role,
    organizations,
    provider,
    contacts,
    lastConnected,
    lastUsedPlatform,
  } = item as User;

  const { sms: smsSettings, ...notificationSettings } =
    item.notificationSettings;

  const editLink = `/public-accounts/edit/${item.id}`;

  const { t } = useTranslation();

  const limit = 2;

  return (
    <div className="responsive-flex-lg px-4 py-4 mt-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all">
      <div className="flex flex-col w-full">
        <Link
          to={editLink}
          className={`flex flex-col flex-1 items-end ${
            !canEditItem ? "pointer-events-none " : ""
          }`}
        >
          <div className="responsive-flex-lg w-full">
            {role?.name !== "Authenticated" && (
              <div className="flex relative w-40 h-40 lg:w-16 lg:h-16 shrink-0">
                <div className="absolute -top-2 -left-2">
                  <WorkSiteIcon iconStyle={iconStyle} />
                </div>
                <div className="rounded-lg overflow-hidden w-full">
                  <ImageComponent
                    image={
                      avatar ? ImageFromStrapiMedia(avatar)?.uri : undefined
                    }
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col flex-1 justify-between lg:pl-4 lg:pr-6">
              <div className="flex gap-3">
                <div className="flex-1 flex flex-col gap-2 py-3 lg:py-0">
                  <h4 className="text-xl font-bold text-primary leading-4 mb-1">
                    {fullname}
                  </h4>

                  {email && (
                    <div className="flex items-center gap-1 text-sm">
                      <EnvelopeIcon className="w-3 h-3" />
                      <span className="font-bold">{email}</span>
                    </div>
                  )}

                  {phoneNumber && (
                    <div className="flex items-center gap-1 text-sm">
                      <PhoneIcon className="w-3 h-3" />
                      <span className="font-bold">{phoneNumber}</span>
                    </div>
                  )}

                  {Constants.IS_MULTI_ORGANIZATIONS &&
                    organizations?.length > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <BuildingOffice2Icon className="w-3 h-3" />
                        <p>
                          {organizations?.map(
                            (o, index) =>
                              `${o.name}${
                                index + 1 < organizations?.length ? ", " : ""
                              }`
                          )}
                        </p>
                      </div>
                    )}

                  {Constants.ENABLE_LOGIN && (
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-slate-500">
                        {t("forms.authenticationProvider")}:
                      </span>
                      <span className="font-bold capitalize">
                        {provider || "local"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {Boolean(item?.followedSites?.length) && (
              <div className="flex flex-col flex-1 gap-2">
                <h4 className="text-l font-semibold text-primary leading-4">
                  {t("common.followedSites", {
                    wording: getWording(true, true),
                  })}
                </h4>

                <div>
                  <TooltipWrapper
                    className="mr-1 my-1"
                    content={
                      item?.followedSites?.length > limit ? (
                        <div className="flex flex-col gap-2">
                          {item?.followedSites?.map((s: Site) => (
                            <ItemFollowedSite site={s} />
                          ))}
                        </div>
                      ) : null
                    }
                  >
                    <div>
                      {item?.followedSites?.map((s: Site, index: number) =>
                        index < limit ? <ItemFollowedSite site={s} /> : null
                      )}

                      {item?.followedSites?.length > limit && (
                        <div className="mt-2">
                          {t("siteUsers.others", {
                            count: (item?.followedSites?.length || 0) - limit,
                          })}
                        </div>
                      )}
                    </div>
                  </TooltipWrapper>
                </div>
              </div>
            )}
          </div>
          <div className="mt-2 mr-1 -mb-1 text-xs italic text-secondary">
            {lastConnected
              ? t("date.lastConnectedOn", {
                  date: dayjs(lastConnected).format("DD/MM/YYYY"),
                  platform: lastUsedPlatform || "?",
                })
              : t("common.noConnection")}
          </div>
        </Link>
        <div className="flex flex-row border-t py-2 mt-2 lg:pl-4">
          {contacts?.map((contact) => (
            <div key={contact.id} className="flex flex-row gap-2">
              <span className="py-1 px-2 bg-secondary rounded-full text-xs text-white">
                {contact?.name}
              </span>
              :
              {contact?.contactGroups?.length
                ? contact?.contactGroups?.map((cg) => (
                    <span
                      key={cg.id}
                      className="px-2 py-1 bg-slate-100 rounded-md text-xs text-slate-700"
                    >
                      {cg.name}
                    </span>
                  ))
                : null}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 min-w-[22em] border-slate-150 gap-2">
        <div className="flex flex-row-reverse flex-wrap justify-between md:flex-col md:justify-start">
          <div className={role ? "flex justify-end gap-2" : ""}>
            {role && <Status status={role?.name} />}
            <Status status={item.confirmed ? "confirmed" : "unconfirmed"} />
          </div>
          <div className="flex flex-row items-center mt-2 self-start">
            <div className="basis-1/2 flex flex-row flex-wrap items-center mt-2">
              {Constants.IS_USERZONE_ENABLED &&
                getStatusIcon(
                  "ZoneIcon",
                  item.areaOfInterest?.length,
                  t("common.areaOfInterestSettings")
                )}
              {item.pushToken
                ? getStatusIcon(
                    "PushIcon",
                    !!item.pushToken,
                    t("common.pushSettings")
                  )
                : getStatusIcon(
                    "MailIcon",
                    true /* mail is always active */,
                    t("common.mailSettings")
                  )}
              {organization?.activeModuleSms
                ? getStatusIcon("SmsIcon", smsSettings, t("common.smsSettings"))
                : null}
            </div>
            <div className="basis-1/2 flex flex-col gap-0">
              <TooltipWrapper
                content={
                  <span className="text-sm">
                    {t("common.postTypesSettings")}
                  </span>
                }
                place="bottom"
              >
                <div>
                  {notificationSettings
                    ? Object.entries(notificationSettings || {}).map(
                        ([notifName, notifStatus]) =>
                          getStatusText(notifName, notifStatus as boolean)
                      )
                    : null}
                </div>
              </TooltipWrapper>
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-end text-slate-500">
          <div>
            <div className="flex w-full gap-1">
              <Button
                type="error-line"
                icon="TrashIcon"
                compact
                onClick={() => onDelete(item.id)}
                disabled={!canEditItem}
                confirmMessage={t("actions.confirmDelete")}
              />

              <Button
                title={t("actions.edit")}
                type="warning-line"
                icon="PencilSquareIcon"
                onClick={() => onNavigate(item.id)}
                disabled={!canEditItem}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserPublicItem;
